import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Plus } from 'lucide-react';

interface MandatoSEPA {
  id: string;
  nombre_titular: string;
  iban: string;
  fecha_firma: string;
  created_at: string;
}

interface MandatoSEPATabProps {
  clienteId: string;
  clienteNombre: string;
}

export const MandatoSEPATab: React.FC<MandatoSEPATabProps> = ({ clienteId, clienteNombre }) => {
  const [mandato, setMandato] = useState<MandatoSEPA | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMandatoSEPA = async () => {
      try {
        const { data, error } = await supabase
          .from('mandatos_sepa')
          .select('*')
          .eq('cliente_id', clienteId)
          .single();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        setMandato(data);
      } catch (error) {
        console.error('Error fetching mandato SEPA:', error);
        toast({
          title: "Error",
          description: "Error al cargar el mandato SEPA",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMandatoSEPA();
  }, [clienteId, toast]);

  const handleCrearMandato = () => {
    // Navigate to MandatoSEPA page with client info pre-filled
    navigate('/mandato-sepa', { 
      state: { 
        clienteId, 
        clienteNombre,
        prefilledData: {
          nombre_titular: clienteNombre
        }
      } 
    });
  };

  const handleDescargarPDF = async () => {
    if (!mandato) return;

    try {
      // Call the PDF generation function (to be implemented)
      toast({
        title: "Descarga iniciada",
        description: "El PDF del mandato SEPA se está generando...",
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: "Error",
        description: "Error al descargar el PDF del mandato SEPA",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!mandato) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Mandato SEPA
          </CardTitle>
          <CardDescription>
            No hay ningún mandato SEPA registrado para este cliente
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleCrearMandato} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Crear Mandato SEPA
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Mandato SEPA
          <Badge variant="secondary">Activo</Badge>
        </CardTitle>
        <CardDescription>
          Información del mandato SEPA registrado
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground">Titular</label>
            <p className="text-sm">{mandato.nombre_titular}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">IBAN</label>
            <p className="text-sm font-mono">{mandato.iban}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">Fecha de Firma</label>
            <p className="text-sm">{new Date(mandato.fecha_firma).toLocaleDateString()}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">Fecha de Registro</label>
            <p className="text-sm">{new Date(mandato.created_at).toLocaleDateString()}</p>
          </div>
        </div>
        
        <div className="flex gap-2 pt-4">
          <Button 
            variant="outline" 
            onClick={handleDescargarPDF}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Descargar PDF
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};